import { useQuery } from "@tanstack/react-query";
import type { SiteContent } from "@shared/schema";

interface ContentInfo {
  value: string;
  contentType: string;
  isRichText: boolean;
}

export function useSiteContent(namespace: string) {
  const query = useQuery<SiteContent[]>({
    queryKey: ["/api/site-content"],
  });

  const getContent = (key: string, defaultValue: string = ""): string => {
    if (!query.data) return defaultValue;
    const content = query.data.find((c) => c.namespace === namespace && c.contentKey === key);
    return content?.value ?? defaultValue;
  };

  const getContentInfo = (key: string, defaultValue: string = ""): ContentInfo => {
    if (!query.data) {
      return { value: defaultValue, contentType: "plain_text", isRichText: false };
    }
    const content = query.data.find((c) => c.namespace === namespace && c.contentKey === key);
    const contentType = content?.contentType ?? "plain_text";
    return {
      value: content?.value ?? defaultValue,
      contentType,
      isRichText: contentType === "rich_text",
    };
  };

  const getJsonContent = <T,>(key: string, defaultValue: T): T => {
    if (!query.data) return defaultValue;
    const content = query.data.find((c) => c.namespace === namespace && c.contentKey === key);
    if (!content?.value) return defaultValue;
    try {
      return JSON.parse(content.value) as T;
    } catch {
      return defaultValue;
    }
  };

  return {
    ...query,
    getContent,
    getContentInfo,
    getJsonContent,
  };
}
